import React, { useEffect, useState } from "react";
import { Table, Switch, Button, Tag, Space, Typography, Spin, Toast,  } from '@douyinfe/semi-ui';
import { ApiClient } from "../../api/ApiClient";

const { Text, Title } = Typography;

interface ScheduledTask {
  id: number;
  taskCode: string;
  taskName: string;
  taskGroup: string;
  cron: string;
  enabled: boolean;
  lastStartAt: string | null;
  lastEndAt: string | null;
  lastStatus: string | null;
  lastResult: string | null;
}

const GROUP_LABELS: Record<string, string> = {
  sync: "同步",
  cleanup: "清理",
  scoring: "评分",
  monitor: "监控",
  system: "系统",
  leads: "客户",
  repayment: "还款",
  default: "默认",
};

const STATUS_COLORS: Record<string, any> = {
  SUCCESS: "green",
  FAILED: "red",
  RUNNING: "blue",
};

export default function ScheduledTaskPage() {
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(false);
  const api = ApiClient.getDefault();

  const loadTasks = async () => {
    setLoading(true);
    try {
      const resp = await api.getScheduledTasks();
      if (resp.code === "ok") {
        setTasks(resp.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const handleToggle = async (taskCode: string, enabled: boolean) => {
    const resp = await api.toggleScheduledTask(taskCode, enabled);
    if (resp.code === "ok") {
      Toast.success(enabled ? "已启用" : "已禁用");
      loadTasks();
    } else {
      Toast.error(resp.msg || "操作失败");
    }
  };

  const handleTrigger = async (taskCode: string) => {
    const resp = await api.triggerScheduledTask(taskCode);
    if (resp.code === "ok") {
      Toast.success("触发成功");
    } else {
      Toast.error(resp.msg || "触发失败");
    }
  };

  const columns = [
    {
      title: "任务名称",
      dataIndex: "taskName",
      width: 180,
    },
    {
      title: "分组",
      dataIndex: "taskGroup",
      width: 80,
      render: (val: string) => GROUP_LABELS[val] || val,
    },
    {
      title: "Cron",
      dataIndex: "cron",
      width: 140,
    },
    {
      title: "启用",
      dataIndex: "enabled",
      width: 80,
      render: (val: boolean, record: ScheduledTask) => (
        <Switch checked={val} onChange={(v) => handleToggle(record.taskCode, v)} />
      ),
    },
    {
      title: "最近状态",
      dataIndex: "lastStatus",
      width: 100,
      render: (val: string | null) => {
        if (!val) return <Text type="tertiary">未执行</Text>;
        return <Tag color={STATUS_COLORS[val] || "grey"}>{val}</Tag>;
      },
    },
    {
      title: "最近开始",
      dataIndex: "lastStartAt",
      width: 170,
      render: (val: string | null) => val ? val.replace("T", " ").substring(0, 19) : "-",
    },
    {
      title: "最近结果",
      dataIndex: "lastResult",
      width: 200,
      ellipsis: true,
      render: (val: string | null) => val || "-",
    },
    {
      title: "操作",
      width: 80,
      render: (_: any, record: ScheduledTask) => (
        <Button size="small" onClick={() => handleTrigger(record.taskCode)}>手动触发</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }}>
        <Title heading={4}>定时任务管理</Title>
        <Button onClick={loadTasks}>刷新</Button>
      </Space>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="taskCode"
          pagination={false}
          size="small"
        />
      </Spin>
    </div>
  );
}
